import { useQueryDashboardData } from './use-query-dashboard-data';
import { DashboardPositions, LiquidationBanner, UnstyledLink, useTokenContext } from '@silo/ui';
import { useSiloWeb3 } from '@silo/web3';
import { match, P } from 'ts-pattern';
import { AssetLogo } from '../silo/common/icons/AssetLogo';
import { isNullable } from 'effect/Predicate';
import { getChecksumAddressOrThrow } from '@silo/types';
import { borrowLink, depositLink, repayLink, withdrawLink } from 'app/hooks/useRoute';
import { getMarketsAtRiskOfLiquidation } from './utils';
import { Rewards } from './rewards';

export function Dashboard(): JSX.Element {
  const { isConnected } = useSiloWeb3();
  const { data: dashboardData } = useQueryDashboardData();

  const marketsAtRiskOfLiquidation = getMarketsAtRiskOfLiquidation(dashboardData);

  return (
    <div className="flex flex-col gap-10">
      {marketsAtRiskOfLiquidation > 0 && (
        <LiquidationBanner
          marketsAtRiskOfLiquidation={marketsAtRiskOfLiquidation}
          href={`#${DashboardPositions.AT_RISK_OF_LIQUIDATION_ID}`}
        />
      )}

      <div className="flex flex-col gap-20">
        {isConnected && <Rewards />}

        {match({ dashboardData, isConnected })
          .with({ isConnected: false }, () => <DashboardPositions.Disconnected />)
          .with({ dashboardData: P.nonNullable }, ({ dashboardData }) => (
            <DashboardPositions
              dashboardData={dashboardData}
              tokenLogo={<TokenLogo />}
              depositMoreLink={<DepositMoreLink />}
              withdrawLink={<WithdrawLink />}
              borrowMoreLink={<BorrowMoreLink />}
              repayLink={<RepayLink />}
              borrowLink={<BorrowLink />}
            />
          ))
          .otherwise(() => (
            <DashboardPositions.Pulse />
          ))}
      </div>
    </div>
  );
}

function TokenLogo(): JSX.Element | null {
  const { protocolInstance } = useSiloWeb3();
  const { tokenAddress } = useTokenContext();

  if (isNullable(protocolInstance)) {
    return null;
  }

  const address = getChecksumAddressOrThrow(tokenAddress);

  return <AssetLogo protocolInstance={protocolInstance} address={address} size="lg" />;
}

const {
  DepositMoreLinkContent,
  WithdrawLinkContent,
  BorrowMoreLinkContent,
  RepayLinkContent,
  BorrowLinkContent,
  useLendingLinkContext,
  lendingLink,
} = DashboardPositions;

function DepositMoreLink(): JSX.Element {
  const { marketAddress, tokenAddress } = useLendingLinkContext();
  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const asset = tokenAddress ? getChecksumAddressOrThrow(tokenAddress) : undefined;
  const href = depositLink(siloAddress, asset);

  return (
    <UnstyledLink data-cy="deposit-card-deposit-button" href={href} className={lendingLink()}>
      <DepositMoreLinkContent />
    </UnstyledLink>
  );
}

function WithdrawLink(): JSX.Element {
  const { marketAddress, tokenAddress } = useLendingLinkContext();
  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const asset = tokenAddress ? getChecksumAddressOrThrow(tokenAddress) : undefined;
  const href = withdrawLink(siloAddress, asset);

  return (
    <UnstyledLink data-cy="deposit-card-withdraw-button" href={href} className={lendingLink()}>
      <WithdrawLinkContent />
    </UnstyledLink>
  );
}

function BorrowMoreLink(): JSX.Element {
  const { marketAddress, tokenAddress } = useLendingLinkContext();
  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const asset = tokenAddress ? getChecksumAddressOrThrow(tokenAddress) : undefined;
  const href = borrowLink(siloAddress, asset);

  return (
    <UnstyledLink data-cy="borrow-card-borrow-button" href={href} className={lendingLink()}>
      <BorrowMoreLinkContent />
    </UnstyledLink>
  );
}

function RepayLink(): JSX.Element {
  const { marketAddress, tokenAddress } = useLendingLinkContext();
  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const asset = tokenAddress ? getChecksumAddressOrThrow(tokenAddress) : undefined;
  const href = repayLink(siloAddress, asset);

  return (
    <UnstyledLink data-cy="borrow-card-repay-button" href={href} className={lendingLink()}>
      <RepayLinkContent />
    </UnstyledLink>
  );
}

function BorrowLink(): JSX.Element {
  const { marketAddress, tokenAddress } = useLendingLinkContext();
  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const asset = tokenAddress ? getChecksumAddressOrThrow(tokenAddress) : undefined;
  const href = borrowLink(siloAddress, asset);

  return (
    <UnstyledLink data-cy="borrow-card-borrow-button" href={href} className={lendingLink()}>
      <BorrowLinkContent />
    </UnstyledLink>
  );
}
